import {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useRef,
} from 'react'
import type { Howl } from 'howler'
import type { StopCallback } from './_types'

interface AudioControllerContextType {
  setCurrentSound: (
    sound: Howl,
    options: {
      onStop: StopCallback
    },
  ) => void
  stopCurrentSound: () => void
  getCurrentSound: () => Howl | null
}

const AudioControllerContext = createContext<AudioControllerContextType | null>(
  null,
)

interface Prop {
  children: ReactNode
}

export const AudioControllerProvider: FC<Prop> = (props) => {
  const { children } = props
  const currentSoundRef = useRef<Howl | null>(null)
  const currentStopCallbackRef = useRef<StopCallback | null>(null)

  const stopCurrentSound = () => {
    if (currentSoundRef.current?.playing()) {
      currentSoundRef.current.stop()
      currentStopCallbackRef.current?.()
    }
    currentSoundRef.current = null
    currentStopCallbackRef.current = null
  }

  const setCurrentSound = (
    sound: Howl,
    options: {
      onStop: StopCallback
    },
  ) => {
    const { onStop } = options
    stopCurrentSound()
    currentSoundRef.current = sound
    if (onStop) {
      currentStopCallbackRef.current = onStop
    }
  }

  const getCurrentSound = () => currentSoundRef.current

  return (
    <AudioControllerContext.Provider
      value={{ stopCurrentSound, setCurrentSound, getCurrentSound }}
    >
      {children}
    </AudioControllerContext.Provider>
  )
}

export const useAudioController = (): AudioControllerContextType => {
  const context = useContext(AudioControllerContext)
  if (!context) {
    throw new Error(
      'useAudioController must be used within an AudioControllerProvider',
    )
  }
  return context
}
